import styled from 'styled-components';
import { baseVariables, toRem } from '../../styles';

const { mediaQuery } = baseVariables;

/** Layout grid is 12 columns at every breakpoint. */
export const GRID_COLUMNS = 12;
/** Mobile: each card takes 10 of 12 grid cols. */
export const MOBILE_CARD_COLS = 10;
/** Tablet: each card takes 5 of 12 grid cols. */
export const TABLET_CARD_COLS = 5;

/**
 * Desktop card span lookup keyed by `desktopCards`. This is the source of
 * truth for the 12-column formula used by Swiper's slidesPerView.
 *
 *   1 card  -> span 12   (full bleed, 1 visible)
 *   2 cards -> span 6    (2 visible)
 *   3 cards -> span 4    (3 visible)
 *   4 cards -> span 3    (4 visible)
 *   6 cards -> span 2    (6 visible)
 */
export const DESKTOP_CARD_COLS: Record<number, number> = {
  1: 12,
  2: 6,
  3: 4,
  4: 3,
  6: 2,
};

/** Amount of the next desktop card shown outside the grid when peeking is enabled. */
export const DESKTOP_PEEK_FRACTION = 0.3;

export const Wrapper = styled.div<{ $peeking?: boolean }>`
  width: 100%;
  max-width: 100%;
  padding-block: 2rem;
  overflow: hidden; /* Clips peeked slides in DevTools responsive mode where viewport clipping is unreliable */
  background-color: var(--t-brand-bg-color);
  color: var(--t-brand-fg-color);

  @media ${mediaQuery.lg} {
    overflow: ${({ $peeking }) => ($peeking ? 'visible' : 'hidden')};
  }
`;

/**
 * 12-column layout grid at every breakpoint. Card column spans differ per
 * breakpoint:
 * - mobile: each card = 10 cols
 * - tablet: each card = 5 cols
 * - desktop: each card = 12 / desktopCards cols
 *
 * All children (header, carousel, controls) span the full grid (1 / -1) so
 * the carousel track and header text snap to the same column edges.
 */
export const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  column-gap: 1rem;
  row-gap: 0;

  @media ${mediaQuery.md} {
    column-gap: 1.5rem;
  }

  @media ${mediaQuery.lg} {
    column-gap: 1.5rem;
  }
`;

export const HeaderWrapper = styled.div`
  grid-column: 1 / -1;
`;

export const CarouselContainer = styled.div<{ $peeking?: boolean }>`
  position: relative;
  grid-column: 1 / -1;
  width: 100%;
  min-width: 0;
  overflow: hidden;

  @media ${mediaQuery.lg} {
    overflow: ${({ $peeking }) => ($peeking ? 'visible' : 'hidden')};
  }
`;

export const SwiperWrapper = styled.div<{ $desktopCards?: number; $peeking?: boolean; $isEditMode?: boolean }>`
  width: 100%;
  min-width: 0;
  padding-block-end: 1rem;

  /* Desktop peeking extends past the 12-col grid; card sizing still comes from Swiper. */
  @media ${mediaQuery.lg} {
    width: ${({ $desktopCards = 3, $peeking, $isEditMode }) =>
      $isEditMode
        ? '100%'
        : $peeking
        ? `calc(100% + (((100% - (${Math.max($desktopCards - 1, 0)} * ${toRem(24)})) / ${$desktopCards} + ${toRem(
            24
          )}) * ${DESKTOP_PEEK_FRACTION}))`
        : '100%'};
  }

  .swiper {
    width: 100%;
    height: auto;
    overflow: hidden;
    display: block;
    position: relative;

    @media ${mediaQuery.lg} {
      overflow: ${({ $peeking }) => ($peeking ? 'visible' : 'hidden')};
    }
  }

  .swiper-slide {
    height: auto;
    display: flex;
    align-items: stretch;
    box-sizing: border-box;
    flex-shrink: 0;
  }

  .swiper-wrapper {
    align-items: stretch;
    flex-wrap: nowrap;
    display: flex;
  }

  ${({ $isEditMode, $desktopCards = 3 }) =>
    $isEditMode &&
    `
    .swiper {
      overflow: visible;
    }

    .swiper-wrapper {
      flex-wrap: wrap;
      gap: ${toRem(24)} ${toRem(24)};
      transform: none !important;
    }

    .swiper-slide {
      margin-block-end: 0;
      margin-right: 0 !important;
      width: calc((100% - (${$desktopCards - 1} * ${toRem(24)})) / ${$desktopCards}) !important;
      flex-shrink: 0;
    }
  `}
`;

export const SlideContent = styled.div`
  width: 100%;
  height: 100%;
  min-height: 100%;
  display: flex;
  flex-direction: column;

  & > *,
  & > * > * {
    flex: 1 1 auto;
    width: 100% !important;
    min-width: 0;
  }

  [data-component-name='o-card-browse'] a {
    // EditableComponent injects extra wrapper divs pushing the <a> beyond
    // the & > * > * reach; target it explicitly so it fills the slide.
    width: 100% !important;
    max-width: 100% !important;
  }

  [data-component-name='o-mi-web-ui-kit-OCardBrowse'] {
    width: 100% !important;
    max-width: 100% !important;
  }

  .aem-container.cq-Editable-dom--container {
    padding-left: 0 !important;
    padding-right: 0 !important;
  }
`;

export const ControlsWrapper = styled.div<{
  $showOnMobile?: boolean;
  $showOnTablet?: boolean;
  $showOnDesktop?: boolean;
}>`
  display: ${({ $showOnMobile = true }) => ($showOnMobile ? 'flex' : 'none')};
  justify-content: center;
  align-items: center;
  margin-block-start: 0;
  padding-inline: 0;

  @media ${mediaQuery.md} {
    display: ${({ $showOnTablet = true }) => ($showOnTablet ? 'flex' : 'none')};
    margin-block-start: 0;
    padding-inline: 0;
  }

  @media ${mediaQuery.lg} {
    display: ${({ $showOnDesktop = true }) => ($showOnDesktop ? 'flex' : 'none')};
    margin-block-start: 0;
    padding-inline: 0;
  }
`;
